import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type GuideTopic = { id: string; name: string };

type AttachedProduct = {
  id: string;
  kind: "affiliate" | "shop";
  title: string;
  brand: string | null;
  role: "used" | "recommended";
  note: string | null;
  position: number;
  affiliateLinkId: string | null;
  shopProductId: string | null;
};

type Guide = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  topicId: string;
  topicName: string | null;
  videoYoutubeId: string | null;
  videoInstagramUrl: string | null;
  bodyMarkdown: string;
  durationMins: number | null;
  difficulty: "easy" | "medium" | "advanced" | null;
  personalNote: string | null;
  featured: boolean;
  sortOrder: number;
  status: "draft" | "published";
};

type GuideSortKey = "title" | "topic" | "difficulty" | "status" | "sort";
type AttachedProductSortKey = "position" | "kind" | "title" | "brand" | "role" | "note";

type FormState = {
  title: string;
  slug: string;
  topicId: string;
  subtitle: string;
  videoYoutubeId: string;
  videoInstagramUrl: string;
  bodyMarkdown: string;
  durationMins: string;
  difficulty: "" | "easy" | "medium" | "advanced";
  personalNote: string;
  featured: boolean;
  sortOrder: string;
  status: "draft" | "published";
};

type AttachState = {
  shopProductId: string;
  affiliateLinkId: string;
  role: "used" | "recommended";
  note: string;
  position: string;
};

const emptyForm: FormState = {
  title: "",
  slug: "",
  topicId: "",
  subtitle: "",
  videoYoutubeId: "",
  videoInstagramUrl: "",
  bodyMarkdown: "",
  durationMins: "",
  difficulty: "",
  personalNote: "",
  featured: false,
  sortOrder: "0",
  status: "draft",
};

const emptyAttach: AttachState = {
  shopProductId: "",
  affiliateLinkId: "",
  role: "used",
  note: "",
  position: "0",
};

function toForm(g: Guide): FormState {
  return {
    title: g.title,
    slug: g.slug,
    topicId: g.topicId,
    subtitle: g.subtitle ?? "",
    videoYoutubeId: g.videoYoutubeId ?? "",
    videoInstagramUrl: g.videoInstagramUrl ?? "",
    bodyMarkdown: g.bodyMarkdown,
    durationMins: g.durationMins?.toString() ?? "",
    difficulty: g.difficulty ?? "",
    personalNote: g.personalNote ?? "",
    featured: g.featured,
    sortOrder: g.sortOrder.toString(),
    status: g.status,
  };
}

function guideSearchText(guide: Guide) {
  return [
    guide.title,
    guide.slug,
    guide.subtitle,
    guide.topicName,
    guide.difficulty,
    guide.status,
    guide.featured ? "featured" : "",
    formatInteger(guide.sortOrder),
  ]
    .filter(Boolean)
    .join(" ");
}

const guideSorters: Record<GuideSortKey, (row: Guide) => string | number> = {
  title: (row) => row.title,
  topic: (row) => row.topicName ?? "",
  difficulty: (row) => row.difficulty ?? "",
  status: (row) => row.status,
  sort: (row) => row.sortOrder,
};

function attachedProductSearchText(product: AttachedProduct) {
  return [
    product.position,
    product.kind,
    product.title,
    product.brand,
    product.role,
    product.note,
  ]
    .filter((value) => value != null)
    .join(" ");
}

const attachedProductSorters: Record<
  AttachedProductSortKey,
  (row: AttachedProduct) => string | number
> = {
  position: (row) => row.position,
  kind: (row) => row.kind,
  title: (row) => row.title,
  brand: (row) => row.brand ?? "",
  role: (row) => row.role,
  note: (row) => row.note ?? "",
};

export default function AdminGuides() {
  const [list, setList] = useState<Guide[]>([]);
  const [topics, setTopics] = useState<GuideTopic[]>([]);
  const [editing, setEditing] = useState<Guide | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [attachedProducts, setAttachedProducts] = useState<AttachedProduct[]>([]);
  const [attach, setAttach] = useState<AttachState>(emptyAttach);
  const [busy, setBusy] = useState(false);
  const [attachBusy, setAttachBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [attachErr, setAttachErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [attachedSearch, setAttachedSearch] = useState("");

  async function refresh() {
    const r = await api.get<{ guides: Guide[] }>("/api/admin/guides");
    setList(r.guides);
  }

  async function refreshAttached(guideId: string) {
    const r = await api.get<{ guide: Guide; attachedProducts: AttachedProduct[] }>(
      `/api/admin/guides/${guideId}`,
    );
    setAttachedProducts(r.attachedProducts);
  }

  useEffect(() => {
    Promise.all([
      refresh(),
      api
        .get<{ guideTopics: GuideTopic[] }>("/api/admin/guide-topics")
        .then((r) => setTopics(r.guideTopics)),
    ]).finally(() => setLoaded(true));
  }, []);

  const topicOptions = useMemo(
    () => [
      { value: "all", label: "All topics" },
      ...topics.map((topic) => ({ value: topic.id, label: topic.name })),
    ],
    [topics],
  );
  const filters = useMemo(
    () => [
      (row: Guide) => statusFilter === "all" || row.status === statusFilter,
      (row: Guide) => topicFilter === "all" || row.topicId === topicFilter,
    ],
    [statusFilter, topicFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Guide, GuideSortKey>({
    rows: list,
    search,
    initialSort: { key: "sort", direction: "asc" },
    sorters: guideSorters,
    searchText: guideSearchText,
    filters,
  });
  const {
    rows: attachedRows,
    sort: attachedSort,
    requestSort: requestAttachedSort,
  } = useAdminTable<AttachedProduct, AttachedProductSortKey>({
    rows: attachedProducts,
    search: attachedSearch,
    initialSort: { key: "position", direction: "asc" },
    sorters: attachedProductSorters,
    searchText: attachedProductSearchText,
  });

  function startNew() {
    setForm({ ...emptyForm, topicId: topics[0]?.id ?? "" });
    setAttachedProducts([]);
    setEditing("new");
    setErr(null);
  }

  function startEdit(g: Guide) {
    setForm(toForm(g));
    setAttach(emptyAttach);
    setEditing(g);
    setErr(null);
    setAttachErr(null);
    refreshAttached(g.id);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      topicId: form.topicId,
      subtitle: form.subtitle || null,
      videoYoutubeId: form.videoYoutubeId || null,
      videoInstagramUrl: form.videoInstagramUrl || null,
      bodyMarkdown: form.bodyMarkdown,
      durationMins: form.durationMins ? parseInt(form.durationMins, 10) : null,
      difficulty: (form.difficulty || null) as "easy" | "medium" | "advanced" | null,
      personalNote: form.personalNote || null,
      featured: form.featured,
      sortOrder: parseInt(form.sortOrder || "0", 10),
      status: form.status,
    };
    try {
      if (editing === "new") {
        const res = await api.post<{ id: string }>("/api/admin/guides", payload);
        await refresh();
        // switch to edit mode for the new guide so user can attach products
        const r = await api.get<{ guide: Guide; attachedProducts: AttachedProduct[] }>(
          `/api/admin/guides/${res.id}`,
        );
        setEditing(r.guide);
        setForm(toForm(r.guide));
        setAttachedProducts(r.attachedProducts);
      } else if (editing) {
        await api.put(`/api/admin/guides/${editing.id}`, payload);
        await refresh();
        setEditing(null);
      }
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(g: Guide) {
    if (!confirm(`Delete "${g.title}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/guides/${g.id}`);
    await refresh();
  }

  async function submitAttach(e: FormEvent) {
    e.preventDefault();
    if (!editing || editing === "new") return;
    setAttachBusy(true);
    setAttachErr(null);
    const payload = {
      shopProductId: attach.shopProductId || null,
      affiliateLinkId: attach.affiliateLinkId || null,
      role: attach.role,
      note: attach.note || null,
      position: parseInt(attach.position || "0", 10),
    };
    try {
      await api.post(`/api/admin/guides/${editing.id}/products`, payload);
      await refreshAttached(editing.id);
      setAttach(emptyAttach);
    } catch (e2) {
      setAttachErr(e2 instanceof Error ? e2.message : "Attach failed");
    } finally {
      setAttachBusy(false);
    }
  }

  async function detach(guideId: string, pid: string) {
    if (!confirm("Remove this product from the guide?")) return;
    await api.del(`/api/admin/guides/${guideId}/products/${pid}`);
    await refreshAttached(guideId);
  }

  const editingGuide = editing !== "new" && editing !== null ? editing : null;

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Guides</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New guide
          </button>
        )}
      </div>

      {editing ? (
        <>
          <form onSubmit={submit} className="paper-card">
            <AdminFormHeader
              title={editing === "new" ? "New guide" : (editing as Guide).title}
              eyebrow={editing === "new" ? "Create guide" : "Edit guide"}
              backLabel="Back to guides"
              onBack={() => setEditing(null)}
            />

            <div className="grid2">
              <Field
                label="Title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                required
              />
              <Field
                label="Slug (optional)"
                value={form.slug}
                onChange={(v) => setForm({ ...form, slug: v })}
              />
            </div>

            <div className="grid2">
              <label className="fld">
                <span>Topic</span>
                <select
                  value={form.topicId}
                  onChange={(e) => setForm({ ...form, topicId: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {topics.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label="Subtitle"
                value={form.subtitle}
                onChange={(v) => setForm({ ...form, subtitle: v })}
              />
            </div>

            <div className="grid2">
              <label className="fld">
                <span>Difficulty</span>
                <select
                  value={form.difficulty}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      difficulty: e.target.value as FormState["difficulty"],
                    })
                  }
                >
                  <option value="">—</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="advanced">Advanced</option>
                </select>
              </label>
              <Field
                label="Duration (mins)"
                value={form.durationMins}
                onChange={(v) => setForm({ ...form, durationMins: v })}
              />
            </div>

            <div className="grid2">
              <Field
                label="YouTube video ID"
                value={form.videoYoutubeId}
                onChange={(v) => setForm({ ...form, videoYoutubeId: v })}
              />
              <Field
                label="Instagram reel URL"
                value={form.videoInstagramUrl}
                onChange={(v) => setForm({ ...form, videoInstagramUrl: v })}
              />
            </div>

            <Field
              label="Body (markdown)"
              value={form.bodyMarkdown}
              onChange={(v) => setForm({ ...form, bodyMarkdown: v })}
              textarea
              tall
              required
            />

            <Field
              label="Personal note"
              value={form.personalNote}
              onChange={(v) => setForm({ ...form, personalNote: v })}
              textarea
            />

            <div className="grid2">
              <label className="fld">
                <span>Status</span>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm({ ...form, status: e.target.value as "draft" | "published" })
                  }
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </select>
              </label>
              <Field
                label="Sort order"
                value={form.sortOrder}
                onChange={(v) => setForm({ ...form, sortOrder: v })}
              />
            </div>

            <label className="fld checkbox">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              <span style={{ marginLeft: 8 }}>Featured</span>
            </label>

            {err && <p className="err">{err}</p>}
            <div className="actions">
              <button type="submit" className="btn-woven" disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>

          {/* Attached products panel — only shown when editing an existing guide */}
          {editingGuide && (
            <div className="paper-card" style={{ marginTop: "1.5rem" }}>
              <legend className="form-legend">Products in this guide</legend>

              {attachedProducts.length > 0 ? (
                <>
                  <AdminTableControls
                    search={attachedSearch}
                    onSearchChange={setAttachedSearch}
                    placeholder="Search attached products..."
                    totalCount={attachedProducts.length}
                    resultCount={attachedRows.length}
                  />
                  {attachedRows.length === 0 ? (
                    <p className="note">No attached products match that search.</p>
                  ) : (
                    <table className="paper-table" style={{ marginBottom: "1.25rem" }}>
                      <thead>
                        <tr>
                          <SortableTh label="Pos" sortKey="position" sort={attachedSort} onSort={requestAttachedSort} />
                          <SortableTh label="Kind" sortKey="kind" sort={attachedSort} onSort={requestAttachedSort} />
                          <SortableTh label="Title" sortKey="title" sort={attachedSort} onSort={requestAttachedSort} />
                          <SortableTh label="Brand" sortKey="brand" sort={attachedSort} onSort={requestAttachedSort} />
                          <SortableTh label="Role" sortKey="role" sort={attachedSort} onSort={requestAttachedSort} />
                          <SortableTh label="Note" sortKey="note" sort={attachedSort} onSort={requestAttachedSort} />
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {attachedRows.map((ap) => (
                          <tr key={ap.id}>
                            <td>{formatInteger(ap.position)}</td>
                            <td>{ap.kind}</td>
                            <td>
                              <strong>{ap.title}</strong>
                            </td>
                            <td>{ap.brand ?? "-"}</td>
                            <td>{ap.role}</td>
                            <td>{ap.note ?? "-"}</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="btn-danger sm"
                                onClick={() => detach(editingGuide.id, ap.id)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <p className="note" style={{ marginBottom: "1rem" }}>
                  No products attached yet.
                </p>
              )}

              <form onSubmit={submitAttach}>
                <legend className="form-legend" style={{ fontSize: "0.9rem" }}>
                  Attach product
                </legend>
                <div className="grid2">
                  <Field
                    label="Shop product ID"
                    value={attach.shopProductId}
                    onChange={(v) => setAttach({ ...attach, shopProductId: v })}
                  />
                  <Field
                    label="Affiliate link ID"
                    value={attach.affiliateLinkId}
                    onChange={(v) => setAttach({ ...attach, affiliateLinkId: v })}
                  />
                </div>
                <div className="grid2">
                  <label className="fld">
                    <span>Role</span>
                    <select
                      value={attach.role}
                      onChange={(e) =>
                        setAttach({ ...attach, role: e.target.value as "used" | "recommended" })
                      }
                    >
                      <option value="used">Used in tutorial</option>
                      <option value="recommended">Recommended</option>
                    </select>
                  </label>
                  <Field
                    label="Position"
                    value={attach.position}
                    onChange={(v) => setAttach({ ...attach, position: v })}
                  />
                </div>
                <Field
                  label="Inline note (optional)"
                  value={attach.note}
                  onChange={(v) => setAttach({ ...attach, note: v })}
                />
                {attachErr && <p className="err">{attachErr}</p>}
                <div className="actions">
                  <button type="submit" className="btn-woven" disabled={attachBusy}>
                    {attachBusy ? "Attaching…" : "Attach"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search guides, topics, slugs..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" },
              ]}
            />
            <AdminTableFilterSelect
              label="Topic"
              value={topicFilter}
              onChange={setTopicFilter}
              options={topicOptions}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No guides yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No guides match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Title" sortKey="title" sort={sort} onSort={requestSort} />
                  <SortableTh label="Topic" sortKey="topic" sort={sort} onSort={requestSort} />
                  <SortableTh label="Difficulty" sortKey="difficulty" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <SortableTh label="Sort" sortKey="sort" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <strong>{g.title}</strong>
                      <span className="slug">{g.slug}</span>
                    </td>
                    <td>{g.topicName ?? "-"}</td>
                    <td>{g.difficulty ?? "-"}</td>
                    <td>{g.status}</td>
                    <td>{formatInteger(g.sortOrder)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(g)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(g)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
  tall,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
  tall?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          style={tall ? { minHeight: 240 } : undefined}
        />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; padding-top: 1.6rem; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
`;
